import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Edit, Trash2, Calendar, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import DOMPurify from 'dompurify';

const NoteCard = ({
  note,
  patients,
  groupes,
  noteStatuses,
  statusesEnabled,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  commentsCount,
  onToggleComments,
  showComments,
  patientLabel
}) => {
  const patient = patients.find(p => p.id === note.patient_id);
  const groupe = patient ? groupes.find(g => g.id === patient.groupe_id) : null;
  const status = noteStatuses.find(s => s.id === note.status_id);

  const truncateHtml = (html, maxLength = 200) => {
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(html);
    const text = div.textContent || div.innerText || '';
    if (text.length <= maxLength) return html;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg">{note.titre}</CardTitle>
              {statusesEnabled && status && (
                <Badge 
                  variant="outline"
                  style={{ 
                    backgroundColor: status.color + '20',
                    color: status.color,
                    borderColor: status.color
                  }}
                >
                  {status.name}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {patient && (
                <span className="font-medium">
                  {patient.prenom} {patient.nom}
                </span>
              )}
              {groupe && (
                <Badge 
                  variant="secondary"
                  style={{ 
                    backgroundColor: groupe.couleur + '20',
                    color: groupe.couleur,
                    fontSize: '0.7rem'
                  }}
                >
                  {groupe.nom}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onEdit(note)}
              data-testid={`edit-note-${note.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-red-500 hover:text-red-700"
              onClick={() => onDelete(note.id)}
              data-testid={`delete-note-${note.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div 
          className="prose prose-sm max-w-none text-gray-700"
          dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(isExpanded ? note.contenu : truncateHtml(note.contenu))
          }}
        />
        
        {note.contenu && note.contenu.length > 200 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 text-blue-600"
            onClick={() => onToggleExpand(note.id)}
          >
            {isExpanded ? (
              <>Voir moins <ChevronUp className="h-4 w-4 ml-1" /></>
            ) : (
              <>Voir plus <ChevronDown className="h-4 w-4 ml-1" /></>
            )}
          </Button>
        )}
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {new Date(note.created_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => onToggleComments(note.id)}
          >
            <MessageSquare className="h-4 w-4" />
            {commentsCount || 0} commentaire(s)
            {showComments ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NoteCard;
